import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
  Card,
  CircularProgress,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import properties from "constants/ApplicationConstants";

function GoalSettingsView() {
  const navigate = useNavigate();
  const {empId} = useParams();

  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({open: false, message: "", type: ""});
  const [employeeName, setEmployeeName] = useState("");

  // Skills Data
  const [technicalPrimary, setTechnicalPrimary] = useState([]);
  const [technicalSecondary, setTechnicalSecondary] = useState([]);
  const [competencyPrimary, setCompetencyPrimary] = useState([]);
  const [competencySecondary, setCompetencySecondary] = useState([]);

  useEffect(() => {
    const storedEmpId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
    if (storedEmpId) {
      const parsedManagerId = parseInt(storedEmpId);
      initializeData(parsedManagerId);
    } else {
      showNotification("Manager ID not found", "error");
      setLoading(false);
    }
  }, [empId]);

  const initializeData = async managerId => {
    try {
      setLoading(true);

      const permissionResponse = await GET(`manager/appraisal/current`);

      if (permissionResponse.data && permissionResponse.data.data) {
        const currentAppraisalId = permissionResponse.data.data.appraisalId;

        await fetchEmployeeGoals(currentAppraisalId, empId, managerId);
      } else {
        showNotification("No active appraisal found", "error");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error initializing data:", error);
      showNotification(
        error.response?.data?.message || "Failed to load current appraisal",
        "error"
      );
      setLoading(false);
    }
  };

  const fetchEmployeeGoals = async (appraisalId, employeeId, managerId) => {
    try {
      const response = await GET(`manager/${appraisalId}/employee/${employeeId}/goals`);

      if (response.data && response.data.data) {
        const goalsData = response.data.data;

        setEmployeeName(goalsData.employeeName || "");
        setTechnicalPrimary(goalsData.technicalPrimary || []);
        setTechnicalSecondary(goalsData.technicalSecondary || []);
        setCompetencyPrimary(goalsData.competencyPrimary || []);
        setCompetencySecondary(goalsData.competencySecondary || []);
      }
    } catch (error) {
      console.error("Error fetching employee goals:", error);
      showNotification(error.response?.data?.message || "Failed to load employee goals", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({open: true, message, type});
    setTimeout(() => {
      setNotification({open: false, message: "", type: ""});
    }, 4000);
  };

  const renderSkillPanel = (title, skills) => {
    if (!skills || skills.length === 0) {
      return (
        <Card variant="outlined" sx={{mb: 1.25}}>
          <MDBox p={1} sx={{backgroundColor: "#f5f5f5", borderBottom: "1px solid #ddd"}}>
            <MDTypography variant="subtitle1" fontWeight="medium">
              {title}
            </MDTypography>
          </MDBox>
          <MDBox p={1}>
            <Alert severity="info" sx={{py: 0.25}}>
              <MDTypography variant="body2">No data found</MDTypography>
            </Alert>
          </MDBox>
        </Card>
      );
    }

    return (
      <Card variant="outlined" sx={{mb: 1.25}}>
        <MDBox p={1} sx={{backgroundColor: "#f5f5f5", borderBottom: "1px solid #ddd"}}>
          <MDTypography variant="subtitle1" fontWeight="medium">
            {title}
          </MDTypography>
        </MDBox>
        <MDBox p={1}>
          <Box sx={{border: "1px solid #e0e0e0", borderRadius: 1}}>
            {skills.map((skill, index) => (
              <Box
                key={skill.paFormId}
                sx={{
                  p: 0.5,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  borderBottom: index === skills.length - 1 ? "none" : "1px solid #e0e0e0"
                }}
              >
                <MDTypography variant="body2" fontWeight="medium" sx={{minWidth: "30px"}}>
                  {index + 1}.
                </MDTypography>
                <MDTypography variant="body2">{skill.skillSet}</MDTypography>
              </Box>
            ))}
          </Box>
        </MDBox>
      </Card>
    );
  };

  const hasAnySkills =
    technicalPrimary.length > 0 ||
    technicalSecondary.length > 0 ||
    competencyPrimary.length > 0 ||
    competencySecondary.length > 0;

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          pt={3}
          pb={2}
          pl={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1.5}>
        <Card>
          <MDBox
            variant="gradient"
            sx={{background: properties.colors.bgcolorbluethem}}
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5}
            mt={0.75}
            p={1.25}
            textAlign="center"
          >
            <MDTypography
              variant="h5"
              sx={{fontFamily: properties.fontFamily}}
              fontWeight="medium"
              color="white"
            >
              Goal Settings - {employeeName}
            </MDTypography>
            <MDTypography
              sx={{fontFamily: properties.fontFamily, mt: 0.25}}
              variant="body2"
              color="white"
              opacity={0.8}
              fontSize="0.875rem"
            >
              View assigned goals and objectives
            </MDTypography>
          </MDBox>

          <MDBox p={1.25}>
            {/* Information Panel */}
            <Card variant="outlined" sx={{mb: 1.25, backgroundColor: "#f9f9f9"}}>
              <MDBox p={1}>
                <MDTypography fontSize="0.875rem" fontWeight="regular" color="dark" paragraph mb={0.3}>
                  Goal setting is understood as the process of defining the aims and objectives with
                  certain guidelines to help you achieve certain expected targets.
                </MDTypography>

                <MDTypography fontSize="0.875rem" fontWeight="regular" color="dark" paragraph mb={0.3}>
                  Goals set help you to give directions during your next term plan and gives you a
                  guideline for achieving objectives that your manager expects from you during next
                  term.
                </MDTypography>

                <MDTypography fontSize="0.875rem" fontWeight="regular" color="dark" mb={0}>
                  These can be altered during the tenure by your reporting manager and will be used
                  as KRA&apos;s for following term.
                </MDTypography>
              </MDBox>
            </Card>

            {/* Skills Display */}
            {hasAnySkills ? (
              <MDBox>
                {renderSkillPanel("Technical Primary Skills", technicalPrimary)}
                {renderSkillPanel("Technical Secondary Skills", technicalSecondary)}
                {renderSkillPanel("Competency Primary Skills", competencyPrimary)}
                {renderSkillPanel("Competency Secondary Skills", competencySecondary)}
              </MDBox>
            ) : (
              <Alert severity="warning" sx={{fontFamily: properties.fontFamily, py: 0.25}}>
                <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                  No Goals Assigned
                </MDTypography>
                <MDTypography variant="body2">
                  No goals have been assigned to this employee yet. Please contact your manager.
                </MDTypography>
              </Alert>
            )}

            <MDBox display="flex" justifyContent="flex-end" mt={1.25}>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => navigate(-1)}
                sx={{background: properties.colors.bgcolorbluethem}}
              >
                Back
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        {notification.open && (
          <MDBox
            sx={{
              position: "fixed",
              top: 20,
              right: 20,
              zIndex: 9999,
              minWidth: "300px"
            }}
          >
            <Alert
              severity={notification.type}
              onClose={() => setNotification({open: false, message: "", type: ""})}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default GoalSettingsView;